(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 768;
  var VISIBLE_RATIO = 2 / 3;

  // ── Singleton state ──
  var currentWrapper = null;
  var resizeObserver = null;
  var scrollRafId = null;
  var isMobile = false;
  var isUnstuck = false;

  function isMobileCheck() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  // ── Sticky (normal) state ──
  // allowAnim: true only for section swaps where the fadein is intentional.
  // During scroll re-stick the animation must be suppressed — otherwise the
  // browser fires bl-gallery-fadein (from opacity:0) the moment the
  // --mobile-sticky class is applied, causing a visible flash.
  function applySticky(wrapper, allowAnim) {
    if (!wrapper) return;
    isUnstuck = false;
    wrapper.style.removeProperty('top');
    wrapper.classList.remove('product__media-wrapper--mobile-unstuck');
    if (!allowAnim) {
      wrapper.classList.add('product__media-wrapper--no-anim');
    }
    wrapper.classList.add('product__media-wrapper--mobile-sticky');
    updateStickyTop(wrapper);
    if (!allowAnim) {
      // Remove the suppressor after one frame so future swap animations work.
      requestAnimationFrame(function () {
        wrapper.classList.remove('product__media-wrapper--no-anim');
      });
    }
  }

  function removeAll(wrapper) {
    if (!wrapper) return;
    isUnstuck = false;
    wrapper.classList.remove('product__media-wrapper--mobile-sticky');
    wrapper.classList.remove('product__media-wrapper--mobile-unstuck');
    wrapper.style.removeProperty('--sticky-gallery-top');
    wrapper.style.removeProperty('top');
    window.__blStickyGalleryVisibleBottom = 0;
  }

  function updateStickyTop(wrapper) {
    if (!wrapper) return;
    var galleryHeight = wrapper.offsetHeight;
    var hiddenPortion = galleryHeight * (1 - VISIBLE_RATIO);
    wrapper.style.setProperty('--sticky-gallery-top', '-' + hiddenPortion + 'px');
    // Expose the visible bottom edge so sticky-atc-bar can use it as threshold
    window.__blStickyGalleryVisibleBottom = galleryHeight * VISIBLE_RATIO;
  }

  // ── Core scroll check ─────────────────────────────────────────────────────
  // Two states:
  //   STICKY  – gallery fixed at 2/3, content scrolls beneath it.
  //   UNSTUCK – gallery is position:relative; it scrolls with the page as a
  //             normal content block.
  //
  // Transition STICKY → UNSTUCK  (scroll down):
  //   When the variant_picker's BOTTOM disappears behind the gallery's
  //   visible bottom edge. We capture the visual position, switch to
  //   position:relative, and compute a top offset for zero visual jump.
  //
  // Transition UNSTUCK → STICKY  (scroll back up):
  //   We wait until the gallery has naturally scrolled back to the sticky
  //   threshold (visualTop >= stickyTop + HYSTERESIS). This guarantees:
  //   – No pop-in: the gallery is already visually AT the sticky position.
  //   – No oscillation: the +HYSTERESIS offset shifts the re-stick point
  //     just above the unstick point, so the unstick condition is false
  //     the moment we go sticky again.
  // ──────────────────────────────────────────────────────────────────────────
  var RESTICK_HYSTERESIS = 4; // px above the unstick line to prevent oscillation

  function updatePosition() {
    if (!currentWrapper) return;

    var galleryHeight = currentWrapper.offsetHeight;
    var hiddenBase    = galleryHeight * (1 - VISIBLE_RATIO);
    var visibleBottom = galleryHeight * VISIBLE_RATIO; // px from viewport top
    var stickyTop     = -hiddenBase;                   // CSS top value when sticky

    var variantPicker = document.querySelector('[data-variant-picker-block]');
    var endTarget = variantPicker
      ? variantPicker.nextElementSibling
      : document.querySelector('[data-sticky-gallery-end]');
    if (!endTarget) return;

    var endRect = endTarget.getBoundingClientRect();

    if (!isUnstuck && endRect.bottom <= visibleBottom) {
      // ── STICKY → UNSTUCK ──────────────────────────────────────────────
      isUnstuck = true;
      window.__blStickyGalleryVisibleBottom = 0;

      // 1. Capture current visual position while still sticky
      var stickyVisualTop = currentWrapper.getBoundingClientRect().top;

      // 2. Switch to relative (removes sticky positioning)
      currentWrapper.classList.remove('product__media-wrapper--mobile-sticky');
      currentWrapper.classList.add('product__media-wrapper--mobile-unstuck');

      // 3. Measure where the element falls in natural flow
      var naturalTop = currentWrapper.getBoundingClientRect().top;

      // 4. Apply offset so the gallery stays visually in the same spot (no jump)
      currentWrapper.style.top = (stickyVisualTop - naturalTop) + 'px';

    } else if (isUnstuck) {
      // ── UNSTUCK → STICKY ──────────────────────────────────────────────
      // Only re-stick when the gallery has scrolled back to the sticky
      // position naturally (in relative mode). The hysteresis ensures the
      // re-stick happens slightly ABOVE the unstick point, so the unstick
      // condition (endRect.bottom <= visibleBottom) is already false when
      // we switch — preventing oscillation.
      var visualTop = currentWrapper.getBoundingClientRect().top;
      if (visualTop >= stickyTop + RESTICK_HYSTERESIS) {
        applySticky(currentWrapper);
      }
    }
  }

  function onScroll() {
    if (!isMobile) return;
    if (scrollRafId) cancelAnimationFrame(scrollRafId);
    scrollRafId = requestAnimationFrame(updatePosition);
  }

  function attachToWrapper(wrapper) {
    if (!wrapper) return;

    // Clean up previous
    if (resizeObserver) resizeObserver.disconnect();
    if (currentWrapper && currentWrapper !== wrapper) {
      removeAll(currentWrapper);
    }

    currentWrapper = wrapper;

    if (!isMobile) return;

    applySticky(wrapper, true); // section swap → allow fadein animation

    resizeObserver = new ResizeObserver(function () {
      if (!isUnstuck) {
        updateStickyTop(wrapper);
      }
    });
    resizeObserver.observe(wrapper);
  }

  function onResize() {
    var wasMobile = isMobile;
    isMobile = isMobileCheck();

    if (isMobile && !wasMobile) {
      applySticky(currentWrapper);
      if (resizeObserver && currentWrapper) resizeObserver.observe(currentWrapper);
    } else if (!isMobile && wasMobile) {
      removeAll(currentWrapper);
      if (resizeObserver) resizeObserver.disconnect();
    }
  }

  // ── Init ──
  function init() {
    isMobile = isMobileCheck();

    var wrapper = document.querySelector('.product__media-wrapper');
    attachToWrapper(wrapper);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    // Watch for section swaps: when a new .product__media-wrapper appears
    // in the DOM, immediately apply sticky to it BEFORE the old one is hidden.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;

          var newWrapper = node.querySelector
            ? node.querySelector('.product__media-wrapper')
            : null;

          if (newWrapper && newWrapper !== currentWrapper) {
            attachToWrapper(newWrapper);
          }
        }
      }
    });

    var sectionNode = document.querySelector('.shopify-section.section-main-product')
      || document.querySelector('product-info')?.closest('.shopify-section')
      || document.querySelector('product-info')?.parentElement;

    if (sectionNode && sectionNode.parentNode) {
      observer.observe(sectionNode.parentNode, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
